import { reportError } from "utils/diagnostics";
import React, { useEffect, useMemo, useState } from "react";
import useFirmwareBundles from "hooks/useFirmwareBundles";
import { bundleNeedsModemUpdate, firmwareVersionOptions } from "utils/firmware-bundles";
import ActionBarSelect from "components/action-bar-select/action-bar-select";
import { PageTitle } from "components/common/Text";
import { BoldText, CommonText, TitleText } from "components/common/text/text.component";
import { ButtonText, PrimaryButton } from "components/common/Buttons";
import { PageSection } from "components/common/Wrapper";
import { FieldValues, useForm } from "react-hook-form";
import { onEnter } from "utils/general";
import { TableBodyCell } from "components/table/table.style";
import { TableRow, useTheme } from "@mui/material";
import { currentSoftwareManualDepot, deviceDetailDepot, pendingActionsDepot } from "../software.depot";
import {
  DeviceSoftwareDeviceDataTypes,
  getAvailableSoftware,
  getPendingActions,
  updateDeviceSoftware,
} from "../software.utilities";
import { FormInput, NxtFieldError } from "components/common/Input";
import style from "../software.module.css";
import { showError } from "utils/notifications";
import { Spinner } from "components/common/spinner/spinner.component";
import { Popup } from "components/common/popup/popup.component";
import { TableOptionTypes } from "pages/Devices/Detail/components/form-component/device-detail-form.component";

import CustomTable from "components/table/table.component";
import { DeviceDetailDataTypes, DeviceValueTypes } from "pages/Devices/devices.config";
import { StatusDot } from "pages/Devices/Detail/components/form-component/DeviceDetail.style";
import { PENDING_GRID_COLUMNS, ACTION_JSON, PendingGridColumnsTypes } from "pages/Devices/Detail/device-detail.config";

export default function SoftwareManualPage(): JSX.Element {
  const [deviceDetail, setDeviceDetail] = useState<any>(deviceDetailDepot.data);
  const [currentSoftwareVer, setCurrentSoftwareVer] = useState<string>(currentSoftwareManualDepot.data);
  const [pendingActions, setPendingActions] = useState<DeviceDetailDataTypes[]>(pendingActionsDepot.data);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [isUpdatingSoftware, setIsUpdatingSoftware] = useState<boolean>(false);
  const theme = useTheme();
  const { bundles } = useFirmwareBundles();

  const {
    register,
    reset,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  }: FieldValues = useForm({
    defaultValues: { softwareVersion: currentSoftwareManualDepot.data },
  });

  // Get selected software version
  const selectedSoftwareVersion: string = watch("softwareVersion");

  // Get queried serial number
  const inputSerial: string = watch("inputSerial");

  useEffect(() => {
    const retrievedDeviceDetail = deviceDetailDepot.observable.subscribe({
      next: (data: DeviceValueTypes) => {
        setDeviceDetail(data);
      },
    });

    const retrievedCurrentSoftware = currentSoftwareManualDepot.observable.subscribe({
      next: (softwareVersion: string) => {
        reset({ softwareVersion: softwareVersion });
        setCurrentSoftwareVer(softwareVersion);
      },
    });

    const retrievedPendingActions = pendingActionsDepot.observable.subscribe({
      next: (data: DeviceDetailDataTypes[]) => {
        setPendingActions(data);
      },
    });

    return () => {
      retrievedDeviceDetail.unsubscribe();
      retrievedPendingActions.unsubscribe();
      retrievedCurrentSoftware.unsubscribe();
    };
  }, []);

  const availableVersions = useMemo((): TableOptionTypes[] => {
    if (!deviceDetail?.availableSW || !deviceDetail?.availableSW?.length) {
      return [];
    }
    return firmwareVersionOptions(deviceDetail.availableSW, bundles, currentSoftwareVer);
  }, [deviceDetail, bundles, currentSoftwareVer]);

  const pendingActionsData = useMemo(
    () =>
      pendingActions?.map((item: DeviceDetailDataTypes) => ({
        ...item,
        completed: item.completed ? "Yes" : "No",
      })),
    [pendingActions],
  );

  const saveSelectedSoftware = async (): Promise<void> => {
    const deviceData: DeviceSoftwareDeviceDataTypes = {
      serialNumber: deviceDetail.eiSerialNumber,
      softwareVersion: selectedSoftwareVersion,
    };
    setIsUpdatingSoftware(true);
    try {
      await updateDeviceSoftware(deviceData);
      await getPendingActions(inputSerial);
    } catch (error: any) {
      reportError("software.manual.saveSelectedSoftware", error);
      showError(error.message);
    } finally {
      setIsUpdatingSoftware(false);
    }
  };

  const onSearchClick = async (): Promise<void> => {
    try {
      setIsDataLoading(true);
      await getAvailableSoftware(inputSerial);
      await getPendingActions(inputSerial);
      setIsDataLoading(false);
    } catch (error: any) {
      reportError("software.manual.onSearchClick", error);
      setIsDataLoading(false);
      setPendingActions([]);
      setDeviceDetail([]);
      showError(error.message);
    }
  };

  const getColumnValue = (value: string | number | boolean, columnId: string) => {
    const actionValues: { value: string | number | boolean } = { value };

    // The pending action comes over as a long string. We break it up with the following
    if (columnId === ACTION_JSON && typeof actionValues.value === "string") {
      const regex = /"[^"\\]*(?:\\[\s\S][^"\\]*)*"/g;
      const actionsArray = actionValues.value.match(regex);
      const actionIndex = actionsArray?.indexOf("ei.action");
      const pendingAction = actionIndex && actionsArray && actionsArray[actionIndex + 2].replaceAll('"', "");

      return pendingAction;
    }
    return value;
  };

  return (
    <>
      <PageTitle>Software</PageTitle>
      <PageSection>
        <div className={style.sectionBarContainer}>
          <div className={style.titleTextContainer}>
            <TitleText>Manual-update</TitleText>
          </div>
          <div className={style.titleTextContainer}>
            <div className={style.searchInputWrapper}>
              <FormInput
                onKeyDown={(event: KeyboardEvent) => onEnter(event, handleSubmit(onSearchClick))}
                {...register("inputSerial", {
                  required: "Serial Number is required",
                })}
                error={errors.version}
                placeholder="Search by serial number"
              />
              {errors.version && <NxtFieldError>{errors.version.message}</NxtFieldError>}
            </div>
            <PrimaryButton disabled={isDataLoading || !inputSerial} onClick={onSearchClick}>
              {ButtonText.Search}
            </PrimaryButton>
          </div>
        </div>
      </PageSection>

      <div style={{ paddingTop: "32px" }}>
        <PageSection>
          {Object.keys(deviceDetail).length ? (
            <div className={style.sectionBarContainer} style={{ paddingBottom: "40px" }}>
              <div className={style.serialNumberSection}>
                <div style={{ paddingRight: "16px" }}>
                  <BoldText>Selected Serial No.</BoldText>
                </div>
                <div style={{ paddingRight: "32px" }}>
                  <CommonText>{deviceDetail.eiSerialNumber}</CommonText>
                </div>
                <StatusDot
                  style={{
                    backgroundColor: deviceDetail.online ? theme.palette.success.main : theme.palette.error.main,
                    marginRight: 5,
                  }}
                />
                <BoldText className="results">{deviceDetail.online ? "Online" : "Offline"}</BoldText>
              </div>
              <div className={style.serialNumberSection}>
                <div className={style.softwareSelect}>
                  <ActionBarSelect
                    defaultVal={currentSoftwareVer}
                    actionName="softwareVersion"
                    options={availableVersions}
                    control={control}
                    rules={{ required: "Column field is required" }}
                  />
                </div>
                <PrimaryButton
                  disabled={
                    !deviceDetail.online ||
                    (currentSoftwareVer === selectedSoftwareVersion &&
                      !bundleNeedsModemUpdate(deviceDetail, selectedSoftwareVersion, bundles))
                  }
                  onClick={saveSelectedSoftware}
                >
                  {ButtonText.Update}
                </PrimaryButton>
              </div>
            </div>
          ) : (
            <div />
          )}
          <CustomTable>
            <CustomTable.Header columns={PENDING_GRID_COLUMNS} />

            <CustomTable.Body isLoading={isDataLoading} deviceData={pendingActionsData}>
              {pendingActionsData?.map((row: DeviceDetailDataTypes, i: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`${row.eiSerialNumber}${i}`}>
                  {PENDING_GRID_COLUMNS.map((column: PendingGridColumnsTypes) => {
                    const value = getColumnValue(row[column.id as keyof DeviceDetailDataTypes], column.id);

                    return (
                      <TableBodyCell
                        key={`${column}${column.id}`}
                        align={column.align}
                        style={{ color: "var(--mega-color-gray500)" }}
                      >
                        {column.format && typeof value === "number" ? column.format(value) : value}
                      </TableBodyCell>
                    );
                  })}
                </TableRow>
              ))}
            </CustomTable.Body>
          </CustomTable>
        </PageSection>
      </div>

      <Popup title="Updating software..." open={isUpdatingSoftware}>
        <Spinner />
      </Popup>
    </>
  );
}
